import * as React from 'react';
import { Button, Form } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { Editable, selectUser, useCacheQuery } from 'gossamer-front-end';
import { BookingPaymentTerms, TicketItem } from 'cb-model';
import { EventDetailsSectionLayout } from './EventDetailsSectionLayout';
import { EventDetails, EventErrors, HandleEventDetailsChange, ValidateEventFormField } from './eventDetailsFormTypes';
import { PERSONAL_INSTITUTION_ID } from 'cb-unified';

import { FieldType } from 'gossamer-unified';

type Props<T extends EventDetails> = {
  eventDetails: T;
  handleDetailsChange: HandleEventDetailsChange<T>;
  errors: EventErrors;
  setErrors: React.Dispatch<React.SetStateAction<EventErrors>>;
  institutionId: number;
  isPaidEvent: boolean;
  lockPaidEventChanges?: boolean;
  validateForm: ValidateEventFormField<T>;
};

const toNumberOrZero = (value: string) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const toIntOrZero = (value: string) => {
  const n = parseInt(value, 10);
  return Number.isFinite(n) ? n : 0;
};

export function EventBookingDetailsSection<T extends EventDetails>(props: Props<T>): React.JSX.Element {
  const {
    eventDetails,
    handleDetailsChange,
    errors,
    setErrors,
    institutionId,
    isPaidEvent,
    lockPaidEventChanges,
    validateForm,
  } = props;
  const user = useSelector(selectUser);

  const bookingPaymentTermsQuery = useCacheQuery(
    BookingPaymentTerms,
    {
      where: {
        and: [{ fieldId: 'institution_id', operator: 'eq', value: PERSONAL_INSTITUTION_ID }],
      },
      limit: 100,
    },
    institutionId !== PERSONAL_INSTITUTION_ID,
  );

  const setBookingPaymentTermsId = (value: number | null) => {
    handleDetailsChange('booking_payment_terms_id' as keyof T, value as any);
  };

  const bookingPaymentTermsId = (eventDetails as any)?.booking_payment_terms_id as number | null | undefined;

  React.useEffect(() => {
    if (!isPaidEvent) return;
    if (institutionId !== PERSONAL_INSTITUTION_ID) return;

    const current = bookingPaymentTermsId;
    if (current != null) return;

    const available = bookingPaymentTermsQuery.data || [];
    if (available.length === 0) return;

    const preferred = (available as any[]).find((t) => t.no_canx_refund) || (available as any[])[0];
    setBookingPaymentTermsId(preferred.id);
  }, [institutionId, isPaidEvent, bookingPaymentTermsQuery.data, bookingPaymentTermsId]);

  const priceEnabled = isPaidEvent;

  const tickets = (eventDetails?.tickets || []) as TicketItem[];

  const TITLE_FIELD = React.useMemo(() => ({ seq: 10, type: FieldType.Text, label: 'Title', mandatory: true }), []);
  const PRICE_FIELD = React.useMemo(() => ({ seq: 20, type: FieldType.Decimal, label: 'Price', mandatory: true }), []);
  const QTY_FIELD = React.useMemo(() => ({ seq: 30, type: FieldType.Integer, label: 'Quantity', mandatory: true }), []);

  const updateTicket = (index: number, patch: Partial<TicketItem>) => {
    const next = tickets.map((t, idx) => (idx === index ? ({ ...t, ...patch } as TicketItem) : t));
    handleDetailsChange('tickets' as keyof T, next as any);
    validateForm({ eventDetails, setErrors, key: 'tickets' as keyof T });
  };

  const addTicket = () => {
    const nextId = -1 * (tickets.length + 1);
    const next = [...tickets, { id: nextId, title: 'Ticket', price: 0, quantity: 10 } as TicketItem];
    handleDetailsChange('tickets' as keyof T, next as any);
    validateForm({ eventDetails, setErrors, key: 'tickets' as keyof T });
  };

  const removeTicket = (index: number) => {
    const next = tickets.filter((_, idx) => idx !== index);
    handleDetailsChange('tickets' as keyof T, next as any);
    validateForm({ eventDetails, setErrors, key: 'tickets' as keyof T });
  };

  return (
    <EventDetailsSectionLayout
      title="Tickets and pricing"
      subtitle="Which tickets are available, how much do they cost and what is the capacity of each type?"
    >
      <Form.Group controlId="tickets">
        <Form.Label>Event tickets*</Form.Label>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {!!lockPaidEventChanges && (
            <Form.Text className="text-muted">
              Ticket titles, quantities, and add/remove controls are locked once paid tickets exist. Prices can still be
              updated.
            </Form.Text>
          )}
          {tickets.map((ticket, idx) => (
            <div key={ticket.id ?? idx} style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <Form.Group style={{ flex: '2 1 220px' }}>
                <Form.Label className="small">Title</Form.Label>
                {lockPaidEventChanges ? (
                  <Form.Control value={String(ticket.title || '')} readOnly />
                ) : (
                  <Editable
                    field={TITLE_FIELD as any}
                    value={String(ticket.title || '')}
                    placeholder="Ticket"
                    handleChange={(val) => updateTicket(idx, { title: val })}
                  />
                )}
              </Form.Group>

              <Form.Group style={{ flex: '1 1 140px' }}>
                <Form.Label className="small">Price</Form.Label>
                {priceEnabled ? (
                  <Editable
                    field={PRICE_FIELD as any}
                    value={String(ticket.price ?? 0)}
                    handleChange={(val) => updateTicket(idx, { price: toNumberOrZero(val) })}
                  />
                ) : (
                  <Form.Control type="number" step="0.01" value={String(ticket.price ?? 0)} readOnly />
                )}
              </Form.Group>

              <Form.Group style={{ flex: '1 1 140px' }}>
                <Form.Label className="small">Quantity</Form.Label>
                {lockPaidEventChanges ? (
                  <Form.Control type="number" value={String(ticket.quantity ?? 0)} readOnly />
                ) : (
                  <Editable
                    field={QTY_FIELD as any}
                    value={String(ticket.quantity ?? 0)}
                    handleChange={(val) => updateTicket(idx, { quantity: toIntOrZero(val) })}
                  />
                )}
              </Form.Group>

              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => removeTicket(idx)}
                disabled={!!lockPaidEventChanges}
              >
                Remove
              </Button>
            </div>
          ))}

          <div>
            <Button variant="outline-secondary" size="sm" onClick={addTicket} disabled={!!lockPaidEventChanges}>
              Add ticket
            </Button>
          </div>
        </div>

        {errors.tickets && <Form.Text className="text-danger">{errors.tickets}</Form.Text>}
      </Form.Group>
    </EventDetailsSectionLayout>
  );
}
